import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import 'aos/dist/aos.css';
import AOS from 'aos';
import { Link } from "react-router-dom";
import whobanner from "../img/cares.png";

// ✅ NEW: icons for "Why Choose SecurXpert?" cards
import { FaChartLine, FaUsers, FaBalanceScale, FaLightbulb } from "react-icons/fa";

const Careers = () => {
  const [showScroll, setShowScroll] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    AOS.init({ duration: 1200, once: true });

    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = totalHeight > 0 ? (window.scrollY / totalHeight) * 100 : 0;
      setScrollProgress(progress);
      setShowScroll(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <div className="min-h-screen font-sans bg-white overflow-x-hidden">
      <Helmet>
        <title>Careers – SecurXpert Technologies</title>
        <meta
          name="description"
          content="Our recruitment journey at SecurXpert Technologies is designed to find the right talent the right way – from profile screening and technical evaluations to HR discussions. Discover why you should choose SecurXpert: accelerated career growth, inclusive culture, work-life harmony and empowering innovation."
        />
      </Helmet>

      <Navbar />

      {/* Hero Section */}
      <header className="relative w-full">
        <div
          className="relative w-full h-[317px] bg-center bg-cover bg-no-repeat"
          style={{ backgroundImage: `url(${whobanner})` }}
        >
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-black/70 lg:bg-black/60" />

          {/* Centered text content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-4 z-10">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 drop-shadow-md">
              Careers
            </h1>

            <div className="flex items-center gap-2 text-sm sm:text-base">
              <Link to="/" className="hover:underline">Home</Link>
              <svg
                width="7"
                height="10"
                viewBox="0 0 7 10"
                fill="none"
                className="mt-[2px]"
              >
                <path
                  d="M0.297 0.262C0.105 0.426 -0.002 0.648 0 0.878C0.002 1.109 0.113 1.329 0.308 1.492L4.02 4.604L0.366 7.711C0.173 7.875 0.067 8.097 0.069 8.328C0.071 8.558 0.182 8.778 0.377 8.941C0.474 9.022 0.59 9.086 0.717 9.13C0.844 9.174 0.981 9.196 1.118 9.195C1.255 9.194 1.391 9.171 1.518 9.126C1.644 9.081 1.758 9.016 1.854 8.933L6.232 5.212C6.329 5.131 6.405 5.034 6.457 4.927C6.509 4.821 6.535 4.706 6.534 4.591C6.533 4.476 6.505 4.362 6.451 4.256C6.397 4.15 6.319 4.054 6.221 3.974L1.774 0.254C1.677 0.173 1.561 0.109 1.434 0.065C1.307 0.021 1.171 -0.001 1.033 0C0.896 0.001 0.76 0.024 0.634 0.069C0.507 0.114 0.393 0.18 0.297 0.262Z"
                  fill="white"
                />
              </svg>
              <span>Careers</span>
            </div>
          </div>
        </div>
      </header>

      {/* Recruitment Journey Section */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* Left Content */}
          {/* ✅ CHANGED: self-center -> self-start so heading aligns to top */}
          <div data-aos="fade-right" className="self-start">
            <h2 className="text-indigo-950 text-4xl font-bold font-['Roboto'] mb-4">
              Our Recruitment Journey <br /> Finding the Right Talent, The Right Way
            </h2>
            {/* ✅ Adjusted font size for consistency */}
            <p className="text-zinc-600 text-base sm:text-lg font-normal font-['Barlow'] leading-6">
              At SecurXpert Technologies, we follow a clear, thoughtful hiring process that
              helps talented professionals connect smoothly and confidently to the right roles.
            </p>
          </div>

          {/* Right Cards */}
          {/* ✅ CHANGED: items-stretch to make all cards same height */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
            {/* Profile Screening */}
            <div
              className="bg-indigo-950 rounded-[10px] p-6 text-center h-full flex flex-col"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <div className="flex justify-center mb-4">
                <svg
                  width="30"
                  height="31"
                  viewBox="0 0 30 31"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M8.33333 6.68416C8.33333 4.91141 9.03571 3.21127 10.286 1.95774C11.5362 0.704221 13.2319 0 15 0C16.7681 0 18.4638 0.704221 19.714 1.95774C20.9643 3.21127 21.6667 4.91141 21.6667 6.68416C21.6667 8.4569 20.9643 10.157 19.714 11.4106C18.4638 12.6641 16.7681 13.3683 15 13.3683C13.2319 13.3683 11.5362 12.6641 10.286 11.4106C9.03571 10.157 8.33333 8.4569 8.33333 6.68416ZM8.33333 16.7104C6.1232 16.7104 4.00358 17.5907 2.44078 19.1576C0.877974 20.7245 0 22.8496 0 25.0656C0 26.3951 0.526784 27.6702 1.46447 28.6104C2.40215 29.5505 3.67392 30.0787 5 30.0787H25C26.3261 30.0787 27.5979 29.5505 28.5355 28.6104C29.4732 27.6702 30 26.3951 30 25.0656C30 22.8496 29.122 20.7245 27.5592 19.1576C25.9964 17.5907 23.8768 16.7104 21.6667 16.7104H8.33333Z"
                    fill="white"
                  />
                </svg>
              </div>
              <h3 className="text-white text-xl font-semibold font-['Roboto'] leading-8 mb-2">
                Profile Screening
              </h3>
              <p className="text-white text-base font-normal font-['Barlow'] leading-6">
                Evaluating qualifications, role feasibility &amp; career alignment.
              </p>
            </div>

            {/* Technical Assessments */}
            <div
              className="bg-indigo-950 rounded-[10px] p-6 text-center h-full flex flex-col"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              <div className="flex justify-center mb-4">
                <svg
                  width="40"
                  height="30"
                  viewBox="0 0 40 30"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4 4.18634C4 1.87731 5.79375 0 8 0H32C34.2062 0 36 1.87731 36 4.18634V19.8851H32V4.18634H8V19.8851H4V4.18634ZM0 24.2808C0 23.5874 0.5375 23.0249 1.2 23.0249H38.8C39.4625 23.0249 40 23.5874 40 24.2808C40 27.0542 37.85 29.3044 35.2 29.3044H4.8C2.15 29.3044 0 27.0542 0 24.2808ZM17.5625 11.5778L15.625 13.6056L17.5625 15.6334C18.15 16.2482 18.15 17.2425 17.5625 17.8508C16.975 18.4591 16.025 18.4657 15.4438 17.8508L12.4438 14.7111C11.8563 14.0962 11.8563 13.1019 12.4438 12.4936L15.4438 9.35385C16.0312 8.73898 16.9813 8.73898 17.5625 9.35385C18.1437 9.96872 18.15 10.963 17.5625 11.5713V11.5778ZM24.5625 9.35385L27.5625 12.4936C28.15 13.1085 28.15 14.1027 27.5625 14.7111L24.5625 17.8508C23.975 18.4657 23.025 18.4657 22.4438 17.8508C21.8625 17.2359 21.8563 16.2417 22.4438 15.6334L24.3813 13.6056L22.4438 11.5778C21.8563 10.963 21.8563 9.96872 22.4438 9.36039C23.0312 8.75206 23.9813 8.74552 24.5625 9.36039V9.35385Z"
                    fill="white"
                  />
                </svg>
              </div>
              <h3 className="text-white text-xl font-semibold font-['Roboto'] leading-8 mb-2">
                Technical Assessments
              </h3>
              <p className="text-white text-base font-normal font-['Barlow'] leading-6">
                Testing logic, domain expertise and coding proficiency.
              </p>
            </div>

            {/* Advanced Technical Evaluation */}
            <div
              className="bg-indigo-950 rounded-[10px] p-6 text-center h-full flex flex-col"
              data-aos="fade-up"
              data-aos-delay="300"
            >
              <div className="flex justify-center mb-4">
                <svg
                  width="30"
                  height="30"
                  viewBox="0 0 30 30"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M27.5 2.5V27.5H2.5V2.5H27.5ZM2.5 0C1.83696 0 1.20107 0.263392 0.732233 0.732233C0.263392 1.20107 0 1.83696 0 2.5L0 27.5C0 28.163 0.263392 28.7989 0.732233 29.2678C1.20107 29.7366 1.83696 30 2.5 30H27.5C28.163 30 28.7989 29.7366 29.2678 29.2678C29.7366 28.7989 30 28.163 30 27.5V2.5C30 1.83696 29.7366 1.20107 29.2678 0.732233C28.7989 0.263392 28.163 0 27.5 0L2.5 0ZM19.145 9.375H24.32C24.695 9.375 25 9.68 25 10.0575V15.23C24.9995 15.3647 24.9591 15.4963 24.8839 15.6081C24.8087 15.72 24.7021 15.807 24.5775 15.8583C24.4529 15.9097 24.3159 15.923 24.1838 15.8966C24.0517 15.8702 23.9303 15.8053 23.835 15.71L22.135 14.01L17.135 19.01C17.0189 19.1264 16.8809 19.2188 16.7291 19.2818C16.5772 19.3448 16.4144 19.3772 16.25 19.3772C16.0856 19.3772 15.9228 19.3448 15.7709 19.2818C15.6191 19.2188 15.4811 19.1264 15.365 19.01L12.5 16.1425L7.135 21.51C6.90028 21.7447 6.58194 21.8766 6.25 21.8766C5.91806 21.8766 5.59972 21.7447 5.365 21.51C5.13028 21.2753 4.99842 20.9569 4.99842 20.625C4.99842 20.2931 5.13028 19.9747 5.365 19.74L11.615 13.49C11.7311 13.3736 11.8691 13.2812 12.0209 13.2182C12.1728 13.1552 12.3356 13.1228 12.5 13.1228C12.6644 13.1228 12.8272 13.1552 12.9791 13.2182C13.1309 13.2812 13.2689 13.3736 13.385 13.49L16.25 16.3575L20.365 12.24L18.665 10.54C18.5697 10.4447 18.5048 10.3233 18.4784 10.1912C18.452 10.0591 18.4653 9.92208 18.5167 9.7975C18.568 9.67291 18.655 9.56631 18.7669 9.49112C18.8787 9.41594 19.0103 9.37553 19.145 9.375Z" fill="white" />
                </svg>
              </div>
              <h3 className="text-white text-xl font-semibold font-['Roboto'] leading-8 mb-2">
                Advanced Technical Evaluation
              </h3>
              <p className="text-white text-base font-normal font-['Barlow'] leading-6">
                Assessing expertise at basic, advanced &amp; expert levels.
              </p>
            </div>

            {/* HR Discussion */}
            <div
              className="bg-indigo-950 rounded-[10px] p-6 text-center h-full flex flex-col"
              data-aos="fade-up"
              data-aos-delay="400"
            >
              <div className="flex justify-center mb-4">
                <svg
                  width="30"
                  height="31"
                  viewBox="0 0 30 31"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M14.7915 0.753042C15.5173 0.270808 16.368 0.00926953 17.2402 0.000241862C18.1123 -0.00878581 18.9683 0.235086 19.704 0.702192L25.935 4.6565H28.5C28.8978 4.6565 29.2794 4.81407 29.5607 5.09455C29.842 5.37502 30 5.75543 30 6.15208V19.6123C30 20.009 29.842 20.3894 29.5607 20.6698C29.2794 20.9503 28.8978 21.1079 28.5 21.1079H26.22C26.2681 21.8361 26.1136 22.5635 25.7735 23.2098C25.4334 23.8561 24.9209 24.3962 24.2925 24.7706L16.653 29.341C16.1836 29.6216 15.6461 29.7689 15.0988 29.7667C14.5515 29.7646 14.0152 29.6133 13.548 29.3291C13.0217 29.7927 12.349 30.0575 11.6471 30.0775C10.9451 30.0974 10.2584 29.8712 9.7065 29.4383L1.9965 23.3976C1.45756 22.976 1.07958 22.3831 0.9254 21.7176C0.77122 21.0521 0.850126 20.354 1.149 19.7394C0.790811 19.4594 0.501218 19.1018 0.302149 18.6937C0.103081 18.2856 -0.000243865 17.8377 4.32199e-07 17.3839V6.15208C4.32199e-07 5.75543 0.158036 5.37502 0.43934 5.09455C0.720645 4.81407 1.10218 4.6565 1.5 4.6565H8.9205L14.7915 0.753042ZM3.2595 17.5873L4.2975 16.6825C5.18457 15.9088 6.34066 15.513 7.5173 15.5801C8.69394 15.6472 9.79721 16.1719 10.59 17.0414L14.643 21.4863C15.2786 22.1833 15.678 23.0621 15.7846 23.9982C15.8912 24.9342 15.6996 25.8799 15.237 26.7013L22.7505 22.2071C22.9464 22.0887 23.095 21.9062 23.171 21.6908C23.2471 21.4753 23.2458 21.2403 23.1675 21.0256L16.527 11.756C16.3451 11.502 16.0881 11.311 15.792 11.2099C15.4958 11.1088 15.1754 11.1026 14.8755 11.1922L11.1465 12.3064C10.3683 12.5391 9.54145 12.5574 8.75366 12.3594C7.96587 12.1614 7.24654 11.7544 6.672 11.1817L6.2325 10.745C5.83388 10.3476 5.55561 9.84629 5.4296 9.29848C5.30358 8.75066 5.33491 8.17854 5.52 7.64766H3V17.3839L3.2595 17.5873ZM18.0945 3.22673C17.849 3.07076 17.5632 2.98941 17.2721 2.99259C16.9809 2.99578 16.6971 3.08337 16.455 3.24468L8.355 8.62876L8.793 9.06697C8.98471 9.25788 9.22471 9.39346 9.4875 9.45929C9.75028 9.52512 10.0261 9.51875 10.2855 9.44086L14.013 8.32666C14.9124 8.05773 15.8737 8.07609 16.7622 8.37914C17.6506 8.6822 18.4218 9.25478 18.9675 10.0167L24.7725 18.1167H27V7.64766H25.9365C25.3658 7.64759 24.8069 7.4852 24.3255 7.17955L18.0945 3.22673ZM6.2715 18.9318L3.8475 21.0466L11.5575 27.0872L12.615 25.2447C12.7721 24.9702 12.838 24.6532 12.8033 24.3391C12.7685 24.025 12.6349 23.73 12.4215 23.4963L8.37 19.0515C8.10576 18.7618 7.7381 18.587 7.34601 18.5646C6.95392 18.5422 6.56716 18.6741 6.2715 18.9318Z" fill="white" />
                </svg>
              </div>
              <h3 className="text-white text-xl font-semibold font-['Roboto'] leading-8 mb-2">
                HR Discussion
              </h3>
              <p className="text-white text-base font-normal font-['Barlow'] leading-6">
                Understanding expectations, culture fit, policies &amp; benefits.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Why Choose SecurXpert Section */}
      <div className="relative w-full py-16 bg-indigo-950">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-white text-4xl font-bold font-['Roboto'] mb-4">
              Why Choose SecurXpert?
            </h2>
            <p className="text-white text-base font-normal font-['Barlow'] leading-6">
              Your career journey starts here. Let&apos;s build the future together!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Accelerated Career Growth */}
            <div
              className="bg-white rounded-[10px] p-6 flex items-center gap-4 border-l-4 border-indigo-500"
              data-aos="fade-up"
            >
              {/* ✅ Replaced placeholder image with icon */}
              <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center">
                <FaChartLine className="text-indigo-700 text-2xl" />
              </div>
              <div className="flex-1">
                <h3 className="text-indigo-950 text-xl font-semibold font-['Roboto'] leading-8 mb-2">
                  Accelerated Career Growth
                </h3>
                <p className="text-zinc-600 text-base font-normal font-['Barlow'] leading-6">
                  Opportunities that challenge, inspire and advance careers.
                </p>
              </div>
            </div>

            {/* Inclusive & Diverse Work Culture */}
            <div
              className="bg-white rounded-[10px] p-6 flex items-center gap-4 border-l-4 border-indigo-400"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center">
                <FaUsers className="text-indigo-700 text-2xl" />
              </div>
              <div className="flex-1">
                <h3 className="text-indigo-950 text-xl font-semibold font-['Roboto'] leading-8 mb-2">
                  Inclusive &amp; Diverse Work Culture
                </h3>
                <p className="text-zinc-600 text-base font-normal font-['Barlow'] leading-6">
                  A collaborative ecosystem where ideas are valued.
                </p>
              </div>
            </div>

            {/* Work-Life Harmony */}
            <div
              className="bg-white rounded-[10px] p-6 flex items-center gap-4 border-l-4 border-blue-800"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center">
                <FaBalanceScale className="text-indigo-700 text-2xl" />
              </div>
              <div className="flex-1">
                <h3 className="text-indigo-950 text-xl font-semibold font-['Roboto'] leading-8 mb-2">
                  Work-Life Harmony
                </h3>
                <p className="text-zinc-600 text-base font-normal font-['Barlow'] leading-6">
                  A healthy balance that supports both your career growth and your overall well-being.
                </p>
              </div>
            </div>

            {/* Empowering Innovation */}
            <div
              className="bg-white rounded-[10px] p-6 flex items-center gap-4 border-l-4 border-blue-600"
              data-aos="fade-up"
              data-aos-delay="300"
            >
              <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center">
                <FaLightbulb className="text-indigo-700 text-2xl" />
              </div>
              <div className="flex-1">
                <h3 className="text-indigo-950 text-xl font-semibold font-['Roboto'] leading-8 mb-2">
                  Empowering Innovation
                </h3>
                <p className="text-zinc-600 text-base font-normal font-['Barlow'] leading-6">
                  A work environment where your strengths turn into meaningful outcomes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      {/* Premium Scroll to Top Button with Progress Ring */}
      {showScroll && (
        <div
          onClick={scrollToTop}
          className="scroll-top fixed bottom-6 right-6 z-50 cursor-pointer flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-r from-blue-500 to-blue-700 shadow-lg hover:from-blue-600 hover:to-blue-800 transition-all"
        >
          <svg
            className="progress-circle -rotate-90 w-12 h-12 absolute"
            viewBox="-1 -1 102 102"
          >
            <path
              d="M50,1 a49,49 0 0,1 0,98 a49,49 0 0,1 0,-98"
              fill="none"
              stroke="white"
              strokeWidth="4"
              strokeDasharray="307.919"
              strokeDashoffset={307.919 - (307.919 * scrollProgress) / 100}
              style={{ transition: "stroke-dashoffset 0.1s linear" }}
            />
          </svg>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-6 h-6 text-white relative z-10"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        </div>
      )}
    </div>
  );
};

export default Careers;
